import { EnrollmentMasterStatus } from './EnrollmentMasterStatus';
import { StudentInSchoolRecord } from './StudentInSchoolRecord';

/**
 * The enrollment-funnel stages whose card replaces a student's regular card on the main
 * menu, mirroring the parent portal's EnrollmentCardStageService.determineCardStage().
 * Being implemented stage by stage — statuses without a built stage return None, which
 * renders the regular student card.
 */
export enum EnrollmentCardStage {
  None = 'none',
  Applied = 'applied',
  Accepted = 'accepted',
  AcceptedMultiple = 'accepted-multiple',
  Waitlist = 'waitlist',
  Registered = 'registered',
  RegistrationComplete = 'registration-complete',
}

/** No accept/decline decision saved on the record at all (no CRM decision counts as none). */
function hasNoSavedDecision(record: StudentInSchoolRecord): boolean {
  return record.CrmEnrollmentDecision?.AcceptedDecisionValue == null
    && record.CrmEnrollmentDecision?.RegisteredDecisionValue == null;
}

/**
 * @param record the record whose card is being rendered
 * @param allRecords every StudentInSchoolRecord of the same student (unfiltered) — the
 *   "accepted at multiple schools" variant needs to see the sibling offers.
 */
export function determineCardStage(record: StudentInSchoolRecord, allRecords: StudentInSchoolRecord[]): EnrollmentCardStage {
  if (record.MasterStatusID === EnrollmentMasterStatus.Applied) {
    return EnrollmentCardStage.Applied;
  }

  if (record.MasterStatusID === EnrollmentMasterStatus.Waitlist) {
    // An explicit 0 means the parent declined the waitlist spot — no card.
    return record.CrmEnrollmentDecision?.WaitlistDecisionValue === 0
      ? EnrollmentCardStage.None
      : EnrollmentCardStage.Waitlist;
  }

  if (record.MasterStatusID === EnrollmentMasterStatus.Accepted) {
    const decision = record.CrmEnrollmentDecision;
    if (decision?.HasDeclinedSeat) {
      return EnrollmentCardStage.None;
    }
    if (decision?.HasConfirmedSeat && record.RegistrationStatus) {
      return (record.RequiredFormsCompletePercent ?? 0) < 100
        ? EnrollmentCardStage.Registered
        : EnrollmentCardStage.RegistrationComplete;
    }

    // Accepted at more than one school, at least one offer still undecided, and no
    // decision saved on this record → the "multiple schools" card (no school name).
    const acceptedRecords = allRecords.filter(r => r.MasterStatusID === EnrollmentMasterStatus.Accepted);
    if (acceptedRecords.length > 1 && acceptedRecords.some(hasNoSavedDecision) && hasNoSavedDecision(record)) {
      return EnrollmentCardStage.AcceptedMultiple;
    }

    // One blue card for the three click-time sub-states (no decision yet / decided but
    // unconfirmed / seat confirmed awaiting registration).
    return EnrollmentCardStage.Accepted;
  }

  return EnrollmentCardStage.None;
}
